import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ROLE_LABELS, type Role } from "@easyshop/shared";
import { api, ApiError } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { queryClient } from "@/lib/query-client";
import { Button, Loading } from "@/components/ui";

interface Info {
  organization: string;
  role: Role;
  email: string;
  invitedBy: string;
}

/** Acceptation d'une invitation : connexion / création de compte puis rattachement. */
export function InvitationPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const session = authClient.useSession();
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<Info>(`/invitations/${token}`).then(setInfo, (e) => setError(e instanceof Error ? e.message : "Invitation invalide"));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/invitations/${token}/accept`);
      await queryClient.invalidateQueries();
      navigate("/", { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Acceptation impossible");
    } finally {
      setBusy(false);
    }
  };

  const here = `/invitation/${token}`;
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-page px-4">
      <div className="w-full max-w-[420px] rounded-card border border-line bg-white px-6 py-7">
        <div className="font-display text-[20px] font-bold">Invitation</div>
        {!info && !error && <Loading />}
        {error && <div className="mt-4 rounded-[10px] bg-danger-bg px-3 py-2 text-[13px] font-semibold text-danger">{error}</div>}
        {info && (
          <>
            <p className="mt-3 text-[14px] leading-[1.55]">
              {info.invitedBy} vous invite à rejoindre <b>{info.organization}</b> en tant que <b>{ROLE_LABELS[info.role]}</b>.
            </p>
            <p className="mt-1 text-[12.5px] text-muted">Invitation destinée à {info.email}.</p>
            <div className="mt-6 flex flex-col gap-2">
              {session.isPending ? (
                <Loading />
              ) : session.data ? (
                <>
                  <Button onClick={() => void accept()} disabled={busy}>
                    Rejoindre {info.organization}
                  </Button>
                  <div className="text-center text-[12px] text-muted">Connecté·e en tant que {session.data.user.email}</div>
                </>
              ) : (
                <>
                  <Link
                    to={`/login?mode=signup&email=${encodeURIComponent(info.email)}&next=${encodeURIComponent(here)}`}
                    className="rounded-full bg-accent py-[11px] text-center text-[14px] font-bold text-white hover:no-underline"
                  >
                    Créer mon compte
                  </Link>
                  <Link
                    to={`/login?email=${encodeURIComponent(info.email)}&next=${encodeURIComponent(here)}`}
                    className="rounded-full border border-line py-[11px] text-center text-[14px] font-bold text-ink hover:no-underline"
                  >
                    J'ai déjà un compte
                  </Link>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
